// What the Workspace settings screens share: the page (title, one or two
// sentences, then boxes of rows), the names of permissions, features and
// member kinds, and the role ceiling.
import type {
  MemberKind,
  NamedFlag,
  RoleView,
  WorkspaceView,
} from "@trameria/cloud-api/workspace-shared";
import { m } from "@trameria/cloud-i18n";
import {
  DetailHeader,
  DrawerButton,
  PanelBody,
  SettingsTopDock,
  useCornerClear,
  useListDetail,
} from "@trameria/ui-core";
import type { ReactNode } from "react";

export { SettingsPage } from "@trameria/ui-core";

/**
 * The list column of a list-and-detail settings screen (members, roles,
 * invites, spaces): the drawer's button when the sidebar is a drawer (settings
 * screens have no header row to hold it), then the list, scrolling.
 */
export function SettingsList({ children }: { children: ReactNode }) {
  // The drawer's button floats at the corner; the list moves right of it (its
  // title starts 8 px inside, over the rows' text).
  const clear = useCornerClear(16, 8);
  return (
    <>
      <DrawerButton />
      <SettingsTopDock />
      <div
        data-settings-list=""
        className="min-h-0 flex-1 select-text overflow-y-auto px-4 pt-7 pb-10"
        style={clear}
      >
        {children}
      </div>
    </>
  );
}

/**
 * The detail beside a settings list. Beside the list it has no header row (an
 * empty row would only draw a line; the list's title and its own stay level).
 * Alone, the row holds the way back to the list and the drawer's button.
 */
export function SettingsDetail({ children }: { children: ReactNode }) {
  const { split } = useListDetail();
  return (
    <>
      {!split && <DetailHeader />}
      <PanelBody>
        <div className="@container max-w-[640px]">{children}</div>
      </PanelBody>
    </>
  );
}

/** A quiet line in place of what one may not see or change. */
export function Note({ children }: { children: ReactNode }) {
  return (
    <p className="mt-4 text-[12.5px] leading-[1.7] text-faint">{children}</p>
  );
}

/**
 * A checkbox as the control of a settings row, or as a list's pick.
 *
 * A block: in a table cell an inline box sits on the text's baseline, 3px
 * above the row's middle.
 */
export function Check({
  label,
  checked,
  disabled,
  onHover,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  /**
   * A list's pick: hidden until the row (a `group`) is hovered or the box has
   * the keyboard's focus, and shown while checked. A quiet list does not turn
   * into a grid of boxes (the old web's lists). Touch screens, with no hover,
   * always show it.
   */
  onHover?: boolean;
  onChange(on: boolean): void;
}) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className={`block h-[15px] w-[15px] flex-none cursor-pointer accent-accent disabled:cursor-not-allowed ${
        onHover
          ? "opacity-0 checked:opacity-100 focus-visible:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100"
          : ""
      }`}
    />
  );
}

// ---- names ---------------------------------------------------------------------

const PERMISSIONS: Record<string, () => string> = {
  "workspace.manage": () => m.ws_perm_workspace_manage(),
  "workspace.invite": () => m.ws_perm_workspace_invite(),
  "role.manage": () => m.ws_perm_role_manage(),
  "member.manage": () => m.ws_perm_member_manage(),
  "space.manage": () => m.ws_perm_space_manage(),
  "message.write": () => m.ws_perm_message_write(),
  "page.write": () => m.ws_perm_page_write(),
  "issue.manage": () => m.ws_perm_issue_manage(),
  "emoji.create": () => m.ws_perm_emoji_create(),
  "emoji.manage": () => m.ws_perm_emoji_manage(),
};

/** What a permission lets one do; a name the screen does not know shows as it is. */
export const permissionLabel = (name: string) => PERMISSIONS[name]?.() ?? name;

const FEATURES: Record<string, { label(): string; note(): string }> = {
  journal: {
    label: () => m.ws_feature_journal(),
    note: () => m.ws_feature_journal_note(),
  },
  dm: { label: () => m.ws_feature_dm(), note: () => m.ws_feature_dm_note() },
  topic: {
    label: () => m.ws_feature_topic(),
    note: () => m.ws_feature_topic_note(),
  },
  broadcast: {
    label: () => m.ws_feature_broadcast(),
    note: () => m.ws_feature_broadcast_note(),
  },
  document: {
    label: () => m.ws_feature_document(),
    note: () => m.ws_feature_document_note(),
  },
  voice: {
    label: () => m.ws_feature_voice(),
    note: () => m.ws_feature_voice_note(),
  },
  project: {
    label: () => m.ws_feature_project(),
    note: () => m.ws_feature_project_note(),
  },
  issue: {
    label: () => m.ws_feature_issue(),
    note: () => m.ws_feature_issue_note(),
  },
  cycle: {
    label: () => m.ws_feature_cycle(),
    note: () => m.ws_feature_cycle_note(),
  },
};

export const featureLabel = (name: string) => FEATURES[name]?.label() ?? name;

/** The note of a feature's row: what it is, and what it needs. */
export const featureNote = (flag: NamedFlag) =>
  [
    FEATURES[flag.name]?.note(),
    flag.requires
      ? m.ws_feature_requires({ feature: featureLabel(flag.requires) })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * Turning a feature on also turns on what it needs; turning one off also turns
 * off what needs it. `flags` are the candidates of one scope.
 */
export function toggleFeature(
  on: readonly string[],
  flags: readonly NamedFlag[],
  name: string,
  enable: boolean,
): string[] {
  const next = new Set(on);
  if (enable) {
    let at: string | undefined = name;
    while (at && !next.has(at)) {
      next.add(at);
      at = flags.find((f) => f.name === at)?.requires;
    }
  } else {
    const drop = (n: string) => {
      next.delete(n);
      for (const f of flags)
        if (f.requires === n && next.has(f.name)) drop(f.name);
    };
    drop(name);
  }
  return flags.map((f) => f.name).filter((n) => next.has(n));
}

export const kindLabel = (kind: MemberKind) =>
  kind === "guest" ? m.ws_kind_guest() : m.ws_kind_member();

// ---- the role ceiling ----------------------------------------------------------

/**
 * The position at and above which I may not touch a role: my highest role's
 * position (the owner has none).
 */
export function roleCeiling(
  roles: readonly RoleView[],
  view: WorkspaceView,
): number {
  if (view.me.isOwner) return Number.POSITIVE_INFINITY;
  const mine = roles.filter(
    (r) => !r.isDefault && view.me.roleIds.includes(r.id),
  );
  return Math.max(0, ...mine.map((r) => r.position));
}
